import {
  createSurfaceMotion,
  installInteractionMotion
} from "./chunk-5FU5GH5G.js";

// src/react.tsx
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { useRef } from "react";

// src/state-artwork.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var StateArtwork = ({
  kind,
  className
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className,
      viewBox: "0 0 320 200",
      fill: "none",
      "aria-hidden": "true",
      style: { color: "var(--color-brand-default, var(--iden-accent))", maxWidth: "100%" },
      children: [
        /* @__PURE__ */ jsx("ellipse", { cx: "160", cy: "166", rx: "105", ry: "17", fill: "currentColor", opacity: ".035" }),
        /* @__PURE__ */ jsxs("g", { stroke: "currentColor", strokeWidth: "1", children: [
          /* @__PURE__ */ jsx("circle", { cx: "160", cy: "100", r: "84", opacity: ".1" }),
          /* @__PURE__ */ jsx("circle", { cx: "160", cy: "100", r: "65", opacity: ".13", strokeDasharray: "2 6" }),
          /* @__PURE__ */ jsx("path", { d: "M39 100h32m178 0h32M160 4v16m0 160v16", opacity: ".25" })
        ] }),
        /* @__PURE__ */ jsx(
          "rect",
          {
            x: "91",
            y: "46",
            width: "138",
            height: "112",
            rx: "16",
            fill: "var(--iden-surface)",
            stroke: "var(--iden-line)"
          }
        ),
        /* @__PURE__ */ jsx("path", { d: "M91 74h138", stroke: "var(--iden-line)" }),
        /* @__PURE__ */ jsx("circle", { cx: "107", cy: "60", r: "2.5", fill: "currentColor", opacity: ".8" }),
        /* @__PURE__ */ jsx("circle", { cx: "118", cy: "60", r: "2.5", fill: "currentColor", opacity: ".25" }),
        /* @__PURE__ */ jsx("path", { d: "M194 60h19", stroke: "currentColor", strokeOpacity: ".2", strokeLinecap: "round" }),
        /* @__PURE__ */ jsxs("g", { stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", children: [
          kind === "success" && /* @__PURE__ */ jsx("path", { d: "m139 112 15 15 29-31" }),
          kind === "error" && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("path", { d: "m143 95 34 34m0-34-34 34" }),
            /* @__PURE__ */ jsx("circle", { cx: "160", cy: "112", r: "29", strokeWidth: "1", opacity: ".2" })
          ] }),
          kind === "search" && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("circle", { cx: "153", cy: "107", r: "18" }),
            /* @__PURE__ */ jsx("path", { d: "m166 120 19 19" })
          ] }),
          kind === "empty" && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("path", { d: "M131 98h58m-58 14h40m-40 14h29", opacity: ".4" }),
            /* @__PURE__ */ jsx("path", { d: "M184 123v14m-7-7h14" })
          ] }),
          kind === "offline" && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("path", { d: "M133 107a39 39 0 0 1 54 0m-46 9a27 27 0 0 1 37 0m-30 9a16 16 0 0 1 21 0M136 90l48 48" }),
            /* @__PURE__ */ jsx("circle", { cx: "160", cy: "136", r: "1" })
          ] })
        ] }),
        /* @__PURE__ */ jsx("circle", { cx: "239", cy: "88", r: "5", fill: "currentColor", opacity: ".5" }),
        /* @__PURE__ */ jsx("circle", { cx: "83", cy: "132", r: "3", fill: "currentColor", opacity: ".35" })
      ]
    }
  );
};

// src/react.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
gsap.registerPlugin(useGSAP);
var useSurfaceMotion = (ref, scene, isEnabled) => {
  useGSAP(
    () => {
      if (!isEnabled || !ref.current) {
        return;
      }
      return createSurfaceMotion(ref.current);
    },
    { scope: ref, dependencies: [scene, isEnabled], revertOnUpdate: true }
  );
};
var MotionRuntime = ({ isEnabled }) => {
  useGSAP(
    (_context, contextSafe) => {
      if (!isEnabled) {
        return;
      }
      return installInteractionMotion(document.body, contextSafe);
    },
    { dependencies: [isEnabled], revertOnUpdate: true }
  );
  return null;
};
var IdentityOrbit = ({
  className,
  logoUrl,
  isMarkVisible = true
}) => {
  const ref = useRef(null);
  useGSAP(
    () => {
      const media = gsap.matchMedia();
      media.add(
        "(prefers-reduced-motion: no-preference)",
        () => {
          gsap.timeline({ defaults: { duration: 0.6, ease: "power3.out" } }).from(
            "[data-orbit-ring]",
            {
              scale: 0.9,
              opacity: 0,
              transformOrigin: "50% 50%",
              stagger: 0.035,
              clearProps: "all"
            },
            0
          ).fromTo(
            "[data-orbit-node]",
            { opacity: 0 },
            { opacity: 1, duration: 0.35, stagger: 0.035, clearProps: "opacity" },
            0.1
          );
        },
        ref
      );
      return () => {
        media.revert();
      };
    },
    { scope: ref }
  );
  return /* @__PURE__ */ jsxs2("svg", { ref, className, viewBox: "0 0 480 480", fill: "none", "aria-hidden": "true", children: [
    /* @__PURE__ */ jsxs2("g", { stroke: "currentColor", children: [
      /* @__PURE__ */ jsx2("circle", { "data-orbit-ring": "", cx: "240", cy: "240", r: "204", opacity: ".08" }),
      /* @__PURE__ */ jsx2("circle", { "data-orbit-ring": "", cx: "240", cy: "240", r: "154", opacity: ".15" }),
      /* @__PURE__ */ jsx2("circle", { "data-orbit-ring": "", cx: "240", cy: "240", r: "104", opacity: ".24" }),
      /* @__PURE__ */ jsx2("path", { d: "M36 240h140m128 0h140M240 36v140m0 128v140", opacity: ".18", strokeDasharray: "3 8" }),
      /* @__PURE__ */ jsx2(
        "rect",
        {
          x: "190",
          y: "190",
          width: "100",
          height: "100",
          rx: "30",
          fill: "currentColor",
          fillOpacity: ".07",
          strokeOpacity: ".4"
        }
      ),
      logoUrl ? /* @__PURE__ */ jsx2("image", { href: logoUrl, x: "208", y: "208", width: "64", height: "64" }) : isMarkVisible && /* @__PURE__ */ jsxs2("svg", { x: "208", y: "208", width: "64", height: "64", viewBox: "20 20 124 120", stroke: "none", children: [
        /* @__PURE__ */ jsx2("rect", { x: "27.6", y: "59.68", width: "25.71", height: "73.89", rx: "3.57", fill: "currentColor" }),
        /* @__PURE__ */ jsx2("rect", { x: "27.6", y: "26.43", width: "25.71", height: "24.08", rx: "3.57", fill: "currentColor" }),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "currentColor",
            d: "M86.11 26.43h-7.86a3.57 3.57 0 0 0-3.57 3.57v16.94a3.57 3.57 0 0 0 3.57 3.57h3.73c17.99 0 32.57 13.2 32.57 29.49s-14.58 29.49-32.57 29.49h-3.73a3.57 3.57 0 0 0-3.57 3.57V130a3.57 3.57 0 0 0 3.57 3.57h8.45c28.49 0 51.79-22.24 53.47-50.31 1.86-30.9-23.1-56.83-54.06-56.83Z"
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs2("g", { "data-orbit-node": "", fill: "currentColor", children: [
      /* @__PURE__ */ jsx2("circle", { cx: "36", cy: "240", r: "5" }),
      /* @__PURE__ */ jsx2("circle", { cx: "240", cy: "86", r: "6" }),
      /* @__PURE__ */ jsx2("circle", { cx: "344", cy: "240", r: "5" }),
      /* @__PURE__ */ jsx2("circle", { cx: "240", cy: "444", r: "4" }),
      /* @__PURE__ */ jsx2("circle", { cx: "349", cy: "349", r: "5" }),
      /* @__PURE__ */ jsx2("circle", { cx: "131", cy: "131", r: "4" })
    ] })
  ] });
};
export {
  IdentityOrbit,
  MotionRuntime,
  StateArtwork,
  useSurfaceMotion
};
